import React, {useEffect, useState} from 'react';
import {Avatar, Flexbox, Image, Label} from "../../common/fusion-ui";
import {clearAdminSessionData, getAdminSessionData, getAdminSessionField, sendAdminRequest} from "../utils/AdminUtils";
import {AdminGenRouteUrls, AdminKeys, AdminModuleFieldsData, AdminModuleRouteUrls, AdminModules, AdminReqUrls, ConfigConstants} from "../config/AdminConfig";
import {addSize, checkNullStr, getJsonValue, getDefStr, isBoolTrue, multSize} from "../../common/fusion-ui/utils/ReactUtils";
import {useTheme} from "../../common/ProjectThemeProvider";
import {Menu, MenuItem} from "@mui/material";
import {useConfig} from "../context/ConfigProvider";
import useScreenSize from "../../common/fusion-ui/hook/useScreenSize";
import {useToast} from "../../common/fusion-ui/provider/ToastProvider";
import {ThemedIconButton} from "../../common/components";
import {Constants, FsVarVariant, RippleVarVariant} from "../../common/fusion-ui/config/FusionConfig";
import KnPLogo from "../assets/icon/knp_logo.webp";
import {Link} from "react-router-dom";
import {printError, printLog} from "../../common/CommonUtils";

/**
 * AdminNavbar component - A memoized functional component that renders the
 * admin navigation bar.
 * @param {Function} onSidebarToggle - Callback function to handle sidebar
 * toggle action.
 * @returns {JSX.Element} - The JSX code to render the admin navigation bar.
 */
const AdminNavbar = React.memo(({
                                    onSidebarToggle
                                }) => {
    const TAG = "AdminNavbar";

    const theme = useTheme();
    const {getConfig, isConfigTrue} = useConfig();
    const {isScreenUpSm} = useScreenSize();
    const {showToast, changeToastVariant} = useToast();

    const NAV_ITEM_HEIGHT = '28px';

    const Fields = {
        adminFullName: 'adminFullName',
        adminEmail: 'adminEmail',
        adminMobile: 'adminMobile',
        adminImage: 'adminImage',
        avatarDropdownAnchorEl: 'avatarDropdownAnchorEl',
        isDataFetched: 'isDataFetched',

        addEventSelectedOption: 'addEventSelectedOption',
    };
    const [fieldsData, setFieldsData] = useState({});
    const openMenu = Boolean(getField(Fields.avatarDropdownAnchorEl));

    useEffect(() => {
        loadAdminData();
    }, []);

    return (<>
        <div
            className={`position-fixed w-100 py-2 top-0`}
            style={{
                zIndex: 100,
                backgroundColor: theme.bg,
                boxShadow: theme.boxShadowXs,
            }}>
            <Flexbox className={''} justifyAt={'between'} alignAt={'center'}>
                {/*left*/}
                <Flexbox className={''} justifyAt={'start'} alignAt={'center'}>

                    <Flexbox className={'ps-2'} overflow={'hidden'} justifyAt={'between'} alignAt={'center'}
                             style={{width: `${ConfigConstants.drawerWidth}px`}}>
                        {/*icon*/}
                        <Link className={'mx-2'} to={AdminModuleRouteUrls[AdminModules.Dashboard]}>
                            <Image
                                wrapperClassName={''}
                                className={''}
                                src={KnPLogo}
                                width={'auto'}
                                height={multSize(NAV_ITEM_HEIGHT, 1.5)}
                            />
                        </Link>

                        {/*sidebar-toggle*/}
                        <ThemedIconButton
                            className={'w-auto'}
                            borderWidth={0}
                            mx={1}
                            outlined={true}
                            circular={false}
                            icon={isConfigTrue(AdminKeys.tracker_SidebarToggleOn) ? `bi bi-layout-sidebar-inset` : `bi bi-layout-sidebar-inset-reverse`}
                            // icon={isConfigTrue(Keys.tracker_SidebarToggleOn) ? `bi bi-layout-sidebar` : `bi bi-layout-sidebar-reverse`}
                            tooltip={isConfigTrue(AdminKeys.tracker_SidebarToggleOn) ? `Hide Sidebar` : `Show Sidebar`}
                            // size={'x'}
                            width={'auto'}
                            height={NAV_ITEM_HEIGHT}
                            iconSize={FsVarVariant.LG}
                            hoverColor={theme.primaryDark}
                            bgColor={theme.primary10}
                            hoverBgColor={theme.primary20}
                            rippleColor={theme.primary70}
                            onClick={() => {
                                // toast('Sidebar-toggle clicked');
                                if (onSidebarToggle) onSidebarToggle();
                            }}
                        />
                    </Flexbox>

                    {/*mid-controls--responsive*/}
                    {!isScreenResp() && <Flexbox className={'mx-2 ms-3 '}>
                        {/*visit-website btn*/}
                        <ThemedIconButton
                            className={'p-2'}
                            icon={'bi bi-globe2'}
                            circular={true}
                            borderWidth={0}
                            tooltip={'Visit website'}
                            // outlined={true}
                            elasticActive={true}
                            bgColor={'transparent'}
                            color={theme.primary}
                            hoverBgColor={theme.primary20}
                            hoverColor={theme.primaryDark}
                            rippleColor={theme.primary50}
                            size={NAV_ITEM_HEIGHT}
                            iconSize={FsVarVariant.LG}
                            href={'https://www.kolsandpeers.com/'}
                            target={'_blank'}
                        />
                    </Flexbox>}
                </Flexbox>

                {/*right*/}
                <Flexbox className={'mx-2 px-2'}
                         alignAt={'center'} justifyAt={'center'} wrap={'nowrap'}>
                    {/*avatar*/}
                    <Avatar wrapperClassName={''}
                            className={'cursor-pointer'}
                            imgClassName={'cursor-pointer'}
                            bordered={false}
                            variant={theme.primary}
                            size={addSize(NAV_ITEM_HEIGHT, 10)}
                            showInitials={true}
                            nameForInitials={getField(Fields.adminFullName)}
                            onImageClick={(e) => {
                                updateField(Fields.avatarDropdownAnchorEl, e.currentTarget);
                            }}>
                        {getField(Fields.adminImage, Constants.userVector)}
                    </Avatar>
                </Flexbox>
            </Flexbox>
        </div>
        {createAvatarMenu()}
    </>)

    function createAvatarMenu() {
        return <Menu
            className={"mt-1"}
            anchorEl={getField(Fields.avatarDropdownAnchorEl)}
            open={openMenu}
            onClose={closeAvatarMenu}
            PaperProps={{
                style: theme.dropdownMenuStyle
            }}>
            {/*admin-img*/}
            <Avatar
                wrapperClassName={'select-none p-0 px-3 mt-1'}
                bordered={false}
                size={'75px'}
                showInitials={true}
                nameForInitials={getField(Fields.adminFullName)}>
                {getField(Fields.adminImage)}
            </Avatar>

            {/*full-name*/}
            <div className={'d-flex w-100 jc-center px-3 mt-1'}>
                <Label
                    className={'m-0 p-0 fw-semi-bold'}
                    color={theme.text80}>
                    {getField(Fields.adminFullName)}
                </Label>
            </div>

            {/*email*/}
            <div className={'d-flex w-100 jc-center mb-2 px-3 mt-n1'}>
                <Label
                    className={'m-0 p-0'}
                    color={theme.text50}>
                    {getField(Fields.adminEmail)}
                </Label>
            </div>

            {/*My ac*/}
            {/*{createAvatarDropdownAnchorItem({*/}
            {/*    moduleName: AdminModules.MyAccount,*/}
            {/*    iconClass: 'bi bi-person'*/}
            {/*})}*/}

            {/*PeerProfiles link*/}
            {createAvatarDropdownAnchorItem({
                moduleName: 'Visit Website',
                iconClass: 'bi bi-globe2',
                resp: true,
                moduleUrl: 'https://www.kolsandpeers.com/',
                target: '_blank',
            })}

            {/*Logout*/}
            {<MenuItem
                className={`m-0 p-0 bg-t text-primary-hover transition-none`}
                onClick={() => closeAvatarMenu()}>
                <label
                    className={`m-0 p-0 py-1x mb-1x w-100 text-start  d-flex al-center jc-start flex-nowrap cursor-pointer transition-none text-primary-hover highlight-primary-hover rounded-3 select-none ${isScreenResp() ? 'px-2' : 'px-3'}`}
                    style={{color: theme.text, height: '28px'}}
                    onClick={handleLogout}>
                    <span><i className={`bi bi-box-arrow-right fs-normal pe-1`}/></span>
                    <span>{'Logout'}</span>
                </label>
            </MenuItem>}
        </Menu>
    }

    function createAvatarDropdownAnchorItem({
                                                moduleName,
                                                moduleUrl,
                                                href,
                                                iconClass,
                                                resp,
                                                asLink = true,
                                                onClick,
                                                color,
                                                className,
                                                target,
                                                ...rest
                                            }) {
        return ((isBoolTrue(resp) && isScreenResp()) || !isBoolTrue(resp)) && <MenuItem
            className={`w-100x transition-none p-0 m-0x rounded-3 ${checkCurrPageMatch(moduleName) ? '' : `highlight-primary-hover highlight-primary-hover highlight-primary-focus text-primary-hovertext-primary-focus`}`}
            rippleColor={RippleVarVariant.primary}
            diableRipple={true}
            style={{
                height: '28px',
                margin: '0 0 .1rem 0',
                backgroundColor: checkCurrPageMatch(moduleName) ? theme.primaryBlue : '',
                color: checkCurrPageMatch(moduleName) ? theme.primaryBlue : '',
            }}
            onClick={() => {
                closeAvatarMenu();
                if (!checkCurrPageMatch(moduleName)) {
                    // const defaultUrl = getDefStr(getDefStr(moduleUrl, ModuleRouteUrls[moduleName]), window.location.href);
                    // // navigate(defaultUrl);
                    // try {
                    //     // const url = getEncodedUrlWithParam(defaultUrl);
                    //     // log('createAvatarDropdownAnchorItem.onClick.url:', url, 'defaultUrl:', defaultUrl);
                    //     // navigate(url);
                    // } catch (error) {
                    //     logErr('createAvatarDropdownAnchorItem.onClick.error:', error);
                    // }
                }
            }}>
            {(checkCurrPageMatch(moduleName) || !isBoolTrue(asLink)) ? <Label
                className={`w-100 d-flex flex-nowrap fd-row al-center h-100 jc-start text-nowrap px-2 transition-sm rounded-3 m-0 p-0 ${className} ${checkCurrPageMatch(moduleName) ? '' : 'text-primary-hover'}`}
                textAt={'start'}
                widthClass={'100'}
                color={getDefStr(color, theme.textWhite)}
                onClick={() => {
                    if (onClick) onClick();
                }}
                {...rest}
            >
                <i className={`${iconClass} fs-normal ${isScreenResp() ? 'px-0 pe-2' : 'px-1'}`}/>
                <span>{moduleName}</span>
            </Label> : <Link to={getDefStr(moduleUrl, AdminModuleRouteUrls[moduleName])}
                             target={target} rel={`noopener noreferrer`}>
                <label
                    // wrapperClassName={'w-100'}
                    className={`w-100 text-nowrap text-start transition-sm rounded-3 p-0 m-0 px-2 cursor-pointer`}
                    // asLink={false}
                    // textAt={'start'}
                    onClick={(e) => {
                        if (onClick) onClick();
                    }}
                    // href={getDefStr(moduleUrl, ModuleRouteUrls[moduleName])}
                    // target={target}
                    // rel={`noopener noreferrer`}
                    {...rest}
                >
                    <i className={`${iconClass} fs-normal ${isScreenResp() ? 'px-0 pe-2' : 'px-1'}`}/>
                    <span>{moduleName}</span>
                </label>
            </Link>}
        </MenuItem>
    }

    function checkCurrPageMatch(moduleName) {
        return checkNullStr(getCurrPage()) && getCurrPage() == moduleName;
    }

    function getCurrPage() {
        return getConfig(AdminKeys.config_navbarCurrPage);
    }

    async function handleLogout() {
        const fun = 'handleLogout:';
        try {
            await sendAdminRequest({
                reqUrl: AdminReqUrls.admin__logout,
                reqOptions: {userId: getAdminSessionField('userId')},
                onFetched: (fetchedData) => {
                    log(fun, 'fetchedData:', fetchedData);
                    clearAdminSessionData();
                    window.location.href = AdminGenRouteUrls.login;
                },
                onError: (error) => {
                    toast('Logout failed, please try again!');
                    logErr(fun, error);
                },
            });
            // log(fun, 'fetchedData:', fetchedData);
            // clearAdminSessionData();
            // window.location.href = AdminGenRouteUrls.login;
        } catch (error) {
            toast('Logout failed, please try again!');
            logErr(fun, error);
        }
    }

    /**
     * Load admin-profile data from local-storage.
     */
    function loadAdminData() {
        const fun = 'loadAdminData:';
        if (!isFieldTrue(Fields.isDataFetched)) {
            updateField(Fields.isDataFetched, true);
            try {
                const mf = AdminModuleFieldsData[AdminModules.MyAccount];

                const adminData = getAdminSessionData();
                // log(fun, 'adminData:', adminData);

                updateField(Fields.adminFullName, getJsonValue(adminData, mf.fullName.column));
                updateField(Fields.adminEmail, getJsonValue(adminData, mf.email.column));
                updateField(Fields.adminMobile, getJsonValue(adminData, mf.mobile.column));
                updateField(Fields.adminImage, getJsonValue(adminData, mf.image.column));
            } catch (error) {
                logErr(fun, error);
                updateField(Fields.isDataFetched, false);
            }
        }
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function isScreenResp() {
        return isScreenUpSm;
    }

    function closeAvatarMenu() {
        updateField(Fields.avatarDropdownAnchorEl, null);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default AdminNavbar;
